export const ACQUISITION_CHANNELS = [
  'google',
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'bing',
  'email',
  'organic',
  'referral',
  'direct',
  'admin',
  'other',
] as const

export type AcquisitionChannel = (typeof ACQUISITION_CHANNELS)[number]
export type AcquisitionClickIdType = 'gclid' | 'fbclid' | 'ttclid' | 'msclkid'

export interface AcquisitionTouch {
  source: string | null
  medium: string | null
  campaign: string | null
  content: string | null
  term: string | null
  referrer: string | null
  landingPage: string | null
  clickIdType: AcquisitionClickIdType | null
  capturedAt: string
}

export interface StoredAcquisition {
  first: AcquisitionTouch
  last: AcquisitionTouch
}

export const ACQUISITION_CHANNEL_LABELS: Record<AcquisitionChannel | 'unknown', string> = {
  google: 'Google',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  bing: 'Bing',
  email: 'Email',
  organic: 'Căutare organică',
  referral: 'Referral',
  direct: 'Direct',
  admin: 'Admin',
  other: 'Altă sursă',
  unknown: 'Necunoscut',
}

const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const STORAGE_PREFIX = 'ns_acq:'

function clip(value: string | null | undefined, max = 191): string | null {
  const text = String(value || '').replace(/[\u0000-\u001f\u007f]/g, ' ').trim()
  if (!text) return null
  return text.slice(0, max)
}

function param(params: URLSearchParams, key: string): string | null {
  return clip(params.get(key))
}

export function clickIdTypeFromSearch(params: URLSearchParams): AcquisitionClickIdType | null {
  if (param(params, 'gclid') || param(params, 'gbraid') || param(params, 'wbraid')) return 'gclid'
  if (param(params, 'fbclid')) return 'fbclid'
  if (param(params, 'ttclid')) return 'ttclid'
  if (param(params, 'msclkid')) return 'msclkid'
  return null
}

export function isAcquisitionCapturePath(pathname: string, shopSlug = ''): boolean {
  let path = pathname || '/'
  const slug = shopSlug.trim().toLowerCase()
  if (slug && (path === `/${slug}` || path.startsWith(`/${slug}/`))) {
    path = path.slice(slug.length + 1) || '/'
    if (!path.startsWith('/')) path = `/${path}`
  }
  return !path.startsWith('/admin')
    && !path.startsWith('/platform')
    && path !== '/start'
    && !path.startsWith('/start/')
}

function hostFromReferrer(referrer: string | null): string | null {
  if (!referrer) return null
  try {
    const url = referrer.includes('://') ? new URL(referrer) : new URL(`https://${referrer}`)
    return url.hostname.replace(/^www\./, '').toLowerCase()
  }
  catch {
    return null
  }
}

export function referrerIsInternal(referrer: string | null, siteHost: string): boolean {
  const host = hostFromReferrer(referrer)
  if (!host) return true
  const self = siteHost.replace(/^www\./, '').split(':')[0]?.toLowerCase() || ''
  if (!self) return false
  if (host === self) return true
  if (self === 'localhost' || self.endsWith('.localhost')) {
    return host === 'localhost' || host.endsWith('.localhost')
  }
  if (host.endsWith(`.${self}`)) return true
  return false
}

function hasCampaignSignal(params: URLSearchParams): boolean {
  return Boolean(
    param(params, 'utm_source')
    || param(params, 'utm_medium')
    || param(params, 'utm_campaign')
    || clickIdTypeFromSearch(params),
  )
}

function touchFromVisit(input: {
  search: string
  pathname: string
  referrer: string
  siteHost: string
}): AcquisitionTouch | null {
  const params = new URLSearchParams(input.search.startsWith('?') ? input.search.slice(1) : input.search)
  const campaign = hasCampaignSignal(params)
  const externalReferrer = input.referrer && !referrerIsInternal(input.referrer, input.siteHost)
    ? clip(input.referrer, 255)
    : null

  if (!campaign && !externalReferrer) return null

  const landing = clip(`${input.pathname || '/'}${input.search || ''}`, 255)
  return {
    source: param(params, 'utm_source'),
    medium: param(params, 'utm_medium'),
    campaign: param(params, 'utm_campaign'),
    content: param(params, 'utm_content'),
    term: param(params, 'utm_term'),
    referrer: externalReferrer,
    landingPage: landing,
    clickIdType: clickIdTypeFromSearch(params),
    capturedAt: new Date().toISOString(),
  }
}

function storageKey(shopSlug: string): string {
  return `${STORAGE_PREFIX}${shopSlug.trim().toLowerCase() || 'shop'}`
}

function readStore(shopSlug: string): StoredAcquisition | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(storageKey(shopSlug))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAcquisition
    if (!parsed?.last) return null
    return parsed
  }
  catch {
    return null
  }
}

function writeStore(shopSlug: string, value: StoredAcquisition) {
  if (!import.meta.client) return
  try {
    localStorage.setItem(storageKey(shopSlug), JSON.stringify(value))
  }
  catch {
    // quota / private mode
  }
}

function isExpired(touch: AcquisitionTouch | null | undefined): boolean {
  if (!touch?.capturedAt) return false
  const at = Date.parse(touch.capturedAt)
  if (!Number.isFinite(at)) return false
  return Date.now() - at > MAX_AGE_MS
}

export function captureAcquisitionVisit(input: {
  shopSlug: string
  pathname: string
  search: string
  referrer: string
  siteHost: string
}) {
  if (!isAcquisitionCapturePath(input.pathname, input.shopSlug)) return

  const incoming = touchFromVisit(input)
  const stored = readStore(input.shopSlug)
  const now = new Date().toISOString()
  const direct: AcquisitionTouch = {
    source: null,
    medium: null,
    campaign: null,
    content: null,
    term: null,
    referrer: null,
    landingPage: clip(input.pathname, 255),
    clickIdType: null,
    capturedAt: now,
  }

  if (!stored) {
    const first = incoming || direct
    writeStore(input.shopSlug, { first, last: incoming || first })
    return
  }

  const lastExpired = isExpired(stored.last)
  if (incoming) {
    writeStore(input.shopSlug, {
      first: stored.first || incoming,
      last: incoming,
    })
    return
  }

  if (lastExpired) {
    writeStore(input.shopSlug, {
      first: stored.first || direct,
      last: direct,
    })
  }
}

export function getCheckoutAcquisition(shopSlug: string): Record<string, unknown> | undefined {
  const stored = readStore(shopSlug)
  if (!stored?.last) return undefined
  const last = stored.last
  const first = stored.first && stored.first.capturedAt !== last.capturedAt ? stored.first : undefined
  return {
    source: last.source,
    medium: last.medium,
    campaign: last.campaign,
    content: last.content,
    term: last.term,
    referrer: last.referrer,
    landingPage: last.landingPage,
    clickIdType: last.clickIdType,
    capturedAt: last.capturedAt,
    first,
  }
}

export function acquisitionChannelLabel(channel?: string | null): string {
  if (!channel) return ACQUISITION_CHANNEL_LABELS.unknown
  return ACQUISITION_CHANNEL_LABELS[channel as AcquisitionChannel] || channel
}
